import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TitleBar } from "../../../configs/products";
import { ROLE } from "../../../data/enums";
import { AuthenticationContext } from "../../../provider/AuthenticationProvider";
import { StyleButtonWrapper } from "../../../shared/components/TitleRow/Styled";
import { StyledButton, TitleText, TitleWrapper } from "../../../shared/styled/TitleBar";
import AddIcon from "../../../shared/svg/AddIcon";
import DownloadIcon from "../../../shared/svg/DownloadIcon";
import ExportIcon from "../../../shared/svg/ExportIcon";
import ImportIcon from "../../../shared/svg/ImportIcon";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import apiMethods from "../../../utils/api";
import { ColumnMapping, downloadExcel } from "../../../utils/exportUtils";
import { error } from "../../../utils/toast";
import { ExportProductModal } from "./ExportProductModal";
import ExportQuantitySummaryModal from "./ExportQuantitySummaryModal";
import { ImportProductModal } from "./ImportProductModal";
import { canCreateMaterialStockCard } from "../../MaterialList/Title/materialStockCardPermissions";
import ProductStockCardModal from "./ProductStockCardModal";

const Title = () => {
    const [openImportProductModal, setOpenImportProductModal] = useState(false);
    const [openExportProductModal, setOpenExportProductModal] = useState(false);
    const [openExportQuantitySummaryModal, setOpenExportQuantitySummaryModal] = useState(false);
    const [openStockCardModal, setOpenStockCardModal] = useState(false);

    const navigate = useNavigate();
    const {
        user: { role },
    } = useContext(AuthenticationContext);
    const canCreateProduct = role === ROLE.MANAGER;
    const canImportProduct = [
        ROLE.MANAGER,
        ROLE.FIRST_STORAGE_KEEPER,
        ROLE.SECOND_STORAGE_KEEPER,
    ].includes(role);
    const canExportProduct = [
        ROLE.MANAGER,
        ROLE.FIRST_STORAGE_KEEPER,
        ROLE.SECOND_STORAGE_KEEPER,
    ].includes(role);
    const canCreateStockCard = canCreateMaterialStockCard(role);

    // Remove the API hook since we need to call the API directly with parameters
    // const [{ data, isCreating }, createExportQuantity] = api.get(
    //     "/products/quantity-tracking/?"
    // );

    const onClickAddButton = () => {
        navigate("/products/create");
    };

    const handleExportQuantitySummary = async (filters: {
        dateRange: [string | null, string | null];
    }) => {
        const [startDate, endDate] = filters.dateRange;
        try {
            // Read current search/filter criteria from URL (managed by SearchFilterBar/ListView)
            const searchParams = new URLSearchParams(window.location.search);
            const ignoredKeys = new Set(["tab", "page", "page_size"]);

            const urlParams = Object.fromEntries(
                Array.from(searchParams.entries()).filter(([key, value]) => {
                    const isIgnoredKey = ignoredKeys.has(key);
                    const isEmptyValue = value === "" || value == null;

                    return !isIgnoredKey && !isEmptyValue;
                })
            );

            const variables = {
                ...urlParams,
                start_date: startDate,
                end_date: endDate,
            };

            const response = await apiMethods.get("/products/quantity-tracking/", variables);

            if (response && Array.isArray(response)) {
                const columnMapping: ColumnMapping = {
                    product_code: "Mã sản phẩm",
                    name: "Tên sản phẩm",
                    storage: "Kho",
                    start_quantity: "Số lượng tồn đầu",
                    total_imported: "Số lượng nhập kho",
                    total_exported: "Số lượng xuất kho",
                    end_quantity: "Số lượng tồn cuối",
                };

                // Download the excel file
                downloadExcel(
                    response,
                    columnMapping,
                    "Thống kê sản phẩm",
                    `thong_ke_san_pham_${startDate}_${endDate}`
                );
            } else {
                error("Dữ liệu không hợp lệ");
            }
        } catch (err) {
            error("Xuất báo cáo thất bại", err);
        }
    };

    return (
        <TitleWrapper>
            {openImportProductModal && (
                <ImportProductModal
                    open={openImportProductModal}
                    handleClose={() => setOpenImportProductModal(false)}
                />
            )}

            {openExportProductModal && (
                <ExportProductModal
                    open={openExportProductModal}
                    handleClose={() => setOpenExportProductModal(false)}
                />
            )}

            {openExportQuantitySummaryModal && (
                <ExportQuantitySummaryModal
                    open={openExportQuantitySummaryModal}
                    handleClose={() => setOpenExportQuantitySummaryModal(false)}
                    handleConfirm={handleExportQuantitySummary}
                    isLoading={false}
                />
            )}
            {openStockCardModal && (
                <ProductStockCardModal
                    open={openStockCardModal}
                    onClose={() => setOpenStockCardModal(false)}
                />
            )}

            <TitleText>{TitleBar.title}</TitleText>

            <StyleButtonWrapper>
                {canCreateStockCard && (
                    <StyledButton
                        variant="outlined"
                        onClick={() => setOpenStockCardModal(true)}
                        data-testid="create-product-stock-card"
                    >
                        <PictureAsPdfOutlinedIcon fontSize="small" />
                        Tạo thẻ kho
                    </StyledButton>
                )}
                <StyledButton
                    variant="outlined"
                    onClick={() => setOpenExportQuantitySummaryModal(true)}
                >
                    <DownloadIcon />
                    Tải file báo cáo
                </StyledButton>

                {canImportProduct && (
                    <StyledButton
                        variant="outlined"
                        onClick={() => setOpenImportProductModal(true)}
                    >
                        <ImportIcon />
                        Nhập kho
                    </StyledButton>
                )}

                {canExportProduct && (
                    <StyledButton
                        variant="outlined"
                        onClick={() => setOpenExportProductModal(true)}
                    >
                        <ExportIcon />
                        Xuất kho
                    </StyledButton>
                )}

                {canCreateProduct && (
                    <StyledButton variant="contained" onClick={onClickAddButton}>
                        <AddIcon />
                        {TitleBar.addButtonText}
                    </StyledButton>
                )}
            </StyleButtonWrapper>
        </TitleWrapper>
    );
};

export default Title;
